/**
 * <free-shipping-bar>
 * Applies the progress-fill width for the free-shipping progress bar.
 *
 * Progress is computed server-side against the pre-tax cart subtotal (see the snippet),
 * so a single bar is shown to every customer type — no audience toggling here.
 *
 * - The drawer variant re-renders server-side via the cart Section Rendering API,
 *   so connectedCallback re-runs with fresh totals on every cart change.
 * - The announcement variant lives outside the cart section, so it refreshes
 *   itself by re-fetching its section whenever the cart changes.
 *
 * The theme fires different cart events depending on the entry point:
 * product cards / PDP dispatch `cart:refresh`, while the cart drawer's own
 * quantity controls dispatch `pb:cart:refresh`. We listen to both (plus
 * `cart:updated`) and debounce so a burst of events triggers a single,
 * final refetch once the cart has settled.
 */
if (!customElements.get('free-shipping-bar')) {
  const CART_EVENTS = ['cart:refresh', 'pb:cart:refresh', 'cart:updated'];

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.applyProgress();

      if (this.dataset.variant === 'announcement') {
        this.scheduleRefresh = this.scheduleRefresh.bind(this);
        CART_EVENTS.forEach((evt) => document.addEventListener(evt, this.scheduleRefresh));
      }
    }

    disconnectedCallback() {
      if (this.scheduleRefresh) {
        CART_EVENTS.forEach((evt) => document.removeEventListener(evt, this.scheduleRefresh));
      }
      clearTimeout(this.refreshTimer);
    }

    applyProgress() {
      this.querySelectorAll('.c-fs-bar__fill[data-percent]').forEach((fill) => {
        const percent = parseFloat(fill.dataset.percent) || 0;
        requestAnimationFrame(() => {
          fill.style.width = `${percent}%`;
        });
      });
    }

    /* Coalesce bursts of cart events into a single refetch once things settle. */
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 150);
    }

    async refresh() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      try {
        const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
        if (response.ok) {
          const html = await response.text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('free-shipping-bar');
          if (fresh) {
            this.innerHTML = fresh.innerHTML;
            this.applyProgress();
          }
        }
      } catch (error) {
        /* Network/parse failure — keep the current bar rather than breaking the page. */
      }
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
